import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { InfoTooltip } from '../ui/Tooltip';
import { ResponsiveTable, type Column } from '../ui/ResponsiveTable';
import { formatEUR, formatDateFR, type TaxableEvent } from '../../services/frenchTaxCalculator';

interface TransactionListProps {
  events: TaxableEvent[];
  year: number;
  /**
   * V2.E.UI.1 — surfaces V2.E1b's `TaxableEvent.incomeRegime` opt-in
   * to the user. Called when the user changes the tax regime for a
   * reward event. Passing `null` resets to neutral (default).
   * Optional : when omitted, the regime column is not rendered (the
   * list stays read-only as it was pre-V2.E.UI.1).
   */
  onSetIncomeRegime?: (eventId: string, regime: 'PFU' | 'BNC' | null) => void;
}

const REWARD_TYPES = new Set<TaxableEvent['type']>([
  'staking_reward',
  'mining',
  'airdrop',
]);

type FilterType = 'all' | 'purchase' | 'sale' | 'staking_reward' | 'mining' | 'airdrop';
type SortKey = 'date' | 'asset' | 'type' | 'value';
type SortOrder = 'asc' | 'desc';

export function TransactionList({ events, year, onSetIncomeRegime }: TransactionListProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let result = [...events];

    // Filter by type
    if (filter !== 'all') {
      result = result.filter(e => e.type === filter);
    }

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.asset.toLowerCase().includes(term) ||
        e.exchange?.toLowerCase().includes(term) ||
        e.notes?.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'asset':
          comparison = a.asset.localeCompare(b.asset);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'value':
          comparison = a.totalValue - b.totalValue;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [events, filter, sortKey, sortOrder, searchTerm]);

  // Type badge styling
  const getTypeBadge = (type: string) => {
    const styles: Record<string, { bg: string; color: string; label: string }> = {
      purchase: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: t('taxCenter.type.purchase', 'Buy') },
      sale: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: t('taxCenter.type.sale', 'Sell') },
      staking_reward: { bg: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', label: t('taxCenter.type.staking', 'Staking') },
      mining: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: t('taxCenter.type.mining', 'Mining') },
      airdrop: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', label: t('taxCenter.type.airdrop', 'Airdrop') },
      swap_in: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: t('taxCenter.type.swapIn', 'Swap In') },
      swap_out: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: t('taxCenter.type.swapOut', 'Swap Out') },
    };

    const style = styles[type] || { bg: 'var(--hover)', color: 'var(--muted)', label: type };

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        textTransform: 'uppercase',
      }}>
        {style.label}
      </span>
    );
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  return (
    <div>
      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
          <Icons.Search size={16} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--muted)',
          }} />
          <input
            type="text"
            placeholder={t('taxCenter.transactions.search', 'Search transactions...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              fontSize: '14px',
            }}
          />
        </div>

        {/* Type Filter */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--hover)', padding: '4px', borderRadius: '8px' }}>
          {[
            { value: 'all', label: t('taxCenter.filter.all', 'All') },
            { value: 'purchase', label: t('taxCenter.filter.buys', 'Buys') },
            { value: 'sale', label: t('taxCenter.filter.sells', 'Sells') },
            { value: 'staking_reward', label: t('taxCenter.filter.staking', 'Staking') },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value as FilterType)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: filter === opt.value ? 'var(--accent)' : 'transparent',
                color: filter === opt.value ? 'white' : 'var(--muted)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={{ flex: '1' }} />

        {/* Count */}
        <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
          {filteredEvents.length} {t('taxCenter.transactions.count', 'transactions')}
        </span>
      </div>

      {/* V2.I.7 — table migrated to <ResponsiveTable> : table on desktop,
          card layout on mobile (<= 768 px). Sort logic stays here, the
          column metadata declares onHeaderClick + sort indicators inline. */}
      {(() => {
        const sortIndicator = (key: SortKey) =>
          sortKey === key ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : '';

        const dateLabel = t('taxCenter.transactions.date', 'Date');
        const typeLabel = t('taxCenter.transactions.type', 'Type');
        const assetLabel = t('taxCenter.transactions.asset', 'Asset');
        const qtyLabel = t('taxCenter.transactions.quantity', 'Quantity');
        const priceLabel = t('taxCenter.transactions.price', 'Price');
        const valueLabel = t('taxCenter.transactions.value', 'Value');
        const exchangeLabel = t('taxCenter.transactions.exchange', 'Exchange');
        const regimeLabel = t('taxCenter.transactions.regime', 'Régime fiscal');

        const columns: Column<TaxableEvent>[] = [
          {
            key: 'date',
            cardLabel: dateLabel,
            header: <>{dateLabel}{sortIndicator('date')}</>,
            onHeaderClick: () => handleSort('date'),
            isSubtitle: true,
            render: (e) => formatDateFR(e.date),
          },
          {
            key: 'type',
            cardLabel: typeLabel,
            header: <>{typeLabel}{sortIndicator('type')}</>,
            onHeaderClick: () => handleSort('type'),
            render: (e) => getTypeBadge(e.type),
          },
          {
            key: 'asset',
            cardLabel: assetLabel,
            header: <>{assetLabel}{sortIndicator('asset')}</>,
            onHeaderClick: () => handleSort('asset'),
            isTitle: true,
            render: (e) => (
              <span style={{ fontWeight: 600 }}>{e.asset}</span>
            ),
          },
          {
            key: 'quantity',
            cardLabel: qtyLabel,
            header: qtyLabel,
            align: 'right',
            render: (e) => e.quantity.toLocaleString('fr-FR', { maximumFractionDigits: 8 }),
          },
          {
            key: 'pricePerUnit',
            cardLabel: priceLabel,
            header: priceLabel,
            align: 'right',
            render: (e) => formatEUR(e.pricePerUnit),
          },
          {
            key: 'totalValue',
            cardLabel: valueLabel,
            header: <>{valueLabel}{sortIndicator('value')}</>,
            onHeaderClick: () => handleSort('value'),
            align: 'right',
            render: (e) => (
              <span style={{
                fontWeight: 600,
                color: e.type === 'sale' ? '#ef4444' : '#10b981',
              }}>
                {e.type === 'sale' ? '-' : '+'}{formatEUR(e.totalValue)}
              </span>
            ),
          },
          {
            key: 'exchange',
            cardLabel: exchangeLabel,
            header: exchangeLabel,
            render: (e) => e.exchange || '—',
          },
          ...(onSetIncomeRegime
            ? [{
                key: 'incomeRegime',
                cardLabel: regimeLabel,
                header: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {regimeLabel}
                    <InfoTooltip
                      content={
                        <div style={{ maxWidth: '320px', lineHeight: 1.5 }}>
                          <strong>{t('taxCenter.transactions.regimeTooltipTitle', 'Quel régime choisir ?')}</strong>
                          <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                            <li>
                              <strong>PFU 30 % (RCM)</strong> — {t(
                                'taxCenter.transactions.regimeTooltipPFU',
                                'staking passif occasionnel, airdrops automatiques. Imposé à la perception (Art. 200 A CGI, BOFiP-RPPM-PVBMC-30).',
                              )}
                            </li>
                            <li>
                              <strong>BNC (barème)</strong> — {t(
                                'taxCenter.transactions.regimeTooltipBNC',
                                'mining régulier, staking actif (validateur), activité considérée comme habituelle (Art. 92 CGI). Imposé au barème progressif IR.',
                              )}
                            </li>
                            <li>
                              <strong>Neutre</strong> — {t(
                                'taxCenter.transactions.regimeTooltipNeutral',
                                'aucun impôt revenu calculé par le moteur. À utiliser si vous déclarez ces revenus séparément ou si le statut est incertain.',
                              )}
                            </li>
                          </ul>
                          <p style={{ margin: '8px 0 0', fontSize: '11px', opacity: 0.75 }}>
                            {t(
                              'taxCenter.transactions.regimeTooltipDisclaimer',
                              'Information indicative — la doctrine fiscale varie selon la blockchain et la fréquence d\'activité. Consultez un conseiller en cas de doute.',
                            )}
                          </p>
                        </div>
                      }
                    />
                  </span>
                ),
                render: (e: TaxableEvent) => (
                  REWARD_TYPES.has(e.type) ? (
                    <label style={{ display: 'inline-block' }}>
                      <span style={{ position: 'absolute', left: '-9999px' }}>
                        {`${regimeLabel} — ${e.type} ${e.asset} ${formatDateFR(e.date)}`}
                      </span>
                      <select
                        value={e.incomeRegime ?? 'NEUTRAL'}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          onSetIncomeRegime(
                            e.id,
                            v === 'NEUTRAL' ? null : (v as 'PFU' | 'BNC'),
                          );
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-secondary)',
                          color: 'var(--text)',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="NEUTRAL">
                          {t('taxCenter.transactions.regimeNeutral', 'Neutre')}
                        </option>
                        <option value="PFU">
                          {t('taxCenter.transactions.regimePFU', 'PFU 30 % (RCM)')}
                        </option>
                        <option value="BNC">
                          {t('taxCenter.transactions.regimeBNC', 'BNC (barème)')}
                        </option>
                      </select>
                    </label>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: '12px' }}>—</span>
                  )
                ),
              } as Column<TaxableEvent>]
            : []),
        ];

        return (
          <ResponsiveTable<TaxableEvent>
            data={filteredEvents}
            columns={columns}
            keyExtractor={(e) => e.id}
            caption={t('taxCenter.transactions.tableCaption', 'Liste des transactions taxables')}
            emptyState={
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <Icons.FileText size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
                  {t('taxCenter.transactions.noTransactions', 'No Transactions')}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
                  {t('taxCenter.transactions.importFirst', 'Import your transaction history to get started')}
                </p>
              </div>
            }
          />
        );
      })()}
    </div>
  );
}
